import { TableCell } from '@ovComponents/2-component/table/table';
import { AmountField } from '@ovComponents/3-pattern/amountField/amountField';
import { NumberField } from '@ovComponents/3-pattern/numberField/numberField';
import {
  type AllocationUnit,
  AllocationUnits,
  displayAmountCentsFromFraction,
  fractionToDisplayPercent,
} from '@utils/allocationUnits';
import { Box, Tooltip } from 'hephaestus/web';
import { InfoRounded as InfoRoundedIcon } from 'hephaestus/webIcons';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ALLOCATION_AMOUNT_FIELD_PROPS,
  ALLOCATION_INPUT_STYLE,
  ALLOCATION_PERCENT_FIELD_PROPS,
} from './constants';
import { getPortfolioCustomTargetColumnSx } from './utils';

const ignoreChange = () => undefined;

const REASON_COPY_KEY = {
  notHeld: 'components:householdPortfolio.customTargetNotHeld',
  notTradeable: 'components:householdPortfolio.customTargetNotTradeable',
} as const;

export type DisabledAllocationReason = keyof typeof REASON_COPY_KEY;

export type DisabledAllocationCellProp = {
  unit: AllocationUnit;
  fraction: number;
  mvCents: number;
  reason: DisabledAllocationReason;
  ariaLabel?: string;
};

export const DisabledAllocationCell = ({
  unit,
  fraction,
  mvCents,
  reason,
  ariaLabel,
}: DisabledAllocationCellProp) => {
  const { t } = useTranslation();
  const reasonId = useId();
  const reasonText = t(REASON_COPY_KEY[reason]);
  const describedBy = { 'aria-describedby': reasonId };
  const labelled = ariaLabel ? { 'aria-label': ariaLabel } : {};

  return (
    <TableCell sx={getPortfolioCustomTargetColumnSx()}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
          {unit === AllocationUnits.PERCENT ? (
            <NumberField
              {...ALLOCATION_PERCENT_FIELD_PROPS}
              fullWidth
              number={`${fractionToDisplayPercent(fraction)}`}
              setNumber={ignoreChange}
              disabled
              inputProps={{ ...labelled, ...describedBy }}
            />
          ) : (
            <AmountField
              {...ALLOCATION_AMOUNT_FIELD_PROPS}
              fullWidth
              useFormatAmountValueCents
              amount={`${displayAmountCentsFromFraction(fraction, mvCents)}`}
              setAmount={ignoreChange}
              disabled
              inputProps={{
                style: ALLOCATION_INPUT_STYLE,
                ...labelled,
                ...describedBy,
              }}
            />
          )}
        </Box>
        <Tooltip title={reasonText} placement='top'>
          <Box
            component='span'
            id={reasonId}
            role='img'
            tabIndex={0}
            aria-label={reasonText}
            sx={{ display: 'inline-flex', flexShrink: 0 }}
          >
            <InfoRoundedIcon sx={{ fontSize: 16 }} color='disabled' />
          </Box>
        </Tooltip>
      </Box>
    </TableCell>
  );
};
